import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { formatCount, overLimitMessage } from "../../../convex/lib/fieldLimits"

type FieldShellProps = {
  id?: string
  label: string
  description?: string
  /** Inline validation message, rendered below the control. */
  error?: string
  optional?: boolean
  required?: boolean
  className?: string
  /** Live character count, rendered beside the label. */
  counter?: React.ReactNode
  children: React.ReactNode
}

/**
 * A server-side length limit should never be the first a person hears of it.
 * Counting from the same shared limit — and reusing the server's wording once
 * they are over — means the form says what the mutation would say, before the
 * mutation is called.
 */
function lengthLimitState(
  value: React.ComponentProps<typeof Input>["value"],
  limit: number | undefined,
  label: string,
) {
  if (limit === undefined) return null
  const length = typeof value === "string" ? value.length : 0
  return {
    length,
    over: length > limit,
    message: length > limit ? overLimitMessage(label, length, limit) : undefined,
    counter: `${formatCount(length)} / ${formatCount(limit)}`,
  }
}

function FieldShell({
  id,
  label,
  description,
  error,
  optional,
  required,
  className,
  counter,
  children,
}: FieldShellProps) {
  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      <div className="flex w-full items-center">
        <FieldLabel htmlFor={id}>
          {label}
          {optional ? " (optional)" : ""}
        </FieldLabel>
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-sm font-medium">
            *
          </span>
        ) : null}
        {counter ? (
          <span className="ml-auto text-xs tabular-nums text-muted-foreground group-data-[invalid=true]/field:text-destructive">
            {counter}
          </span>
        ) : null}
      </div>
      {description && <FieldDescription>{description}</FieldDescription>}
      {children}
      {error && <FieldError>{error}</FieldError>}
    </Field>
  )
}

export function FormInput({
  id,
  label,
  description,
  error,
  optional,
  required,
  className,
  limit,
  ...props
}: Omit<FieldShellProps, "children" | "counter"> &
  Omit<React.ComponentProps<typeof Input>, "id"> & {
    id: string
    /** Show a live count and flag the field once it passes this length. */
    limit?: number
  }) {
  const length = lengthLimitState(props.value, limit, label)
  const shownError = error ?? length?.message
  return (
    <FieldShell
      id={id}
      label={label}
      description={description}
      error={shownError}
      optional={optional}
      required={required}
      className={className}
      counter={length?.counter}
    >
      <Input
        id={id}
        aria-invalid={shownError ? true : undefined}
        {...props}
        required={required}
      />
    </FieldShell>
  )
}

export function FormTextarea({
  id,
  label,
  description,
  error,
  optional,
  required,
  className,
  limit,
  ...props
}: Omit<FieldShellProps, "children" | "counter"> &
  Omit<React.ComponentProps<typeof Textarea>, "id"> & {
    id: string
    /** Show a live count and flag the field once it passes this length. */
    limit?: number
  }) {
  const length = lengthLimitState(props.value, limit, label)
  const shownError = error ?? length?.message
  return (
    <FieldShell
      id={id}
      label={label}
      description={description}
      error={shownError}
      optional={optional}
      required={required}
      className={className}
      counter={length?.counter}
    >
      <Textarea
        id={id}
        aria-invalid={shownError ? true : undefined}
        {...props}
        required={required}
      />
    </FieldShell>
  )
}

export function FormSelect<T extends string>({
  id,
  label,
  description,
  optional,
  required,
  className,
  value,
  onValueChange,
  placeholder,
  options,
}: {
  id?: string
  label: string
  description?: string
  optional?: boolean
  required?: boolean
  className?: string
  value: T | ""
  onValueChange: (value: T) => void
  placeholder?: string
  options: readonly { value: T; label: string }[]
}) {
  return (
    <FieldShell
      id={id}
      label={label}
      description={description}
      optional={optional}
      required={required}
      className={className}
    >
      <Select
        value={value || undefined}
        onValueChange={(next) => {
          // Radix's hidden native <select> lives inside <form> and dispatches
          // change="" when the chosen value is not yet a native <option>
          // (content is closed). Ignore that so saved values are not wiped.
          if (!next) return
          onValueChange(next as T)
        }}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  )
}

export function FormFieldGroup({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div className={cn("space-y-4", className)} {...props} />
}
